import { useState } from "react";
import { CalendarClock, FolderOpen } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ProjectCardMenu } from "@/components/projects/project-card-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardFooter } from "@/components/ui/card";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import type { ProjectCardView } from "@/features/projects";

const projectCardDateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

type ProjectCardProps = {
  onArchive: () => void;
  onDelete: () => void;
  onOpen: () => void;
  onSettings: () => void;
  project: ProjectCardView;
};

function formatDate(value: string) {
  if (!value) {
    return "Not updated yet";
  }

  return projectCardDateFormatter.format(new Date(value));
}

export function ProjectCard({
  onArchive,
  onDelete,
  onOpen,
  onSettings,
  project,
}: ProjectCardProps) {
  const { t } = useTranslation();
  const [coverLightboxOpen, setCoverLightboxOpen] = useState(false);
  const coverAlt = `${project.title} cover`;

  return (
    <>
      <Card className="group project-shell-panel flex h-full flex-col overflow-hidden transition-transform duration-300 hover:-translate-y-1 hover:shadow-panel">
        <div className="relative aspect-[4/3] overflow-hidden bg-[linear-gradient(160deg,rgba(237,229,214,0.8),rgba(245,241,232,0.95))]">
          {project.coverImageUrl ? (
            <button
              aria-label={coverAlt}
              className="block h-full w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => setCoverLightboxOpen(true)}
              type="button"
            >
              <img
                alt={coverAlt}
                className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]"
                src={project.coverImageUrl}
              />
            </button>
          ) : (
            <div className="cover-fallback flex h-full items-end justify-start p-5">
              <div className="text-left text-primary-foreground">
                <p className="text-[11px] uppercase tracking-[0.24em] text-primary-foreground/75">
                  {project.genre}
                </p>
                <p className="mt-2 max-w-[12rem] font-display text-3xl leading-none">
                  {project.title}
                </p>
              </div>
            </div>
          )}

          <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[rgba(22,17,13,0.42)] to-transparent" />

          <div className="absolute right-3 top-3 opacity-0 transition-opacity group-hover:opacity-100">
            <ProjectCardMenu
              onArchive={onArchive}
              onDelete={onDelete}
              onSettings={onSettings}
              project={project}
            />
          </div>

          <div className="absolute bottom-3 left-3">
            <Badge
              className="border-white/35 bg-[rgba(255,252,247,0.88)] text-[10px] text-ink backdrop-blur"
              variant={project.status === "archived" ? "archived" : "outline"}
            >
              {project.status === "archived"
                ? t("projects.status.archived")
                : t("projects.status.active")}
            </Badge>
          </div>
        </div>

        <div className="flex-1 space-y-4 px-5 py-5">
          <div className="min-w-0 space-y-1.5">
            <p className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
              {t("projects.portfolio.eyebrow")}
            </p>
            <h3 className="truncate font-display text-2xl font-semibold leading-none text-foreground">
              {project.title}
            </h3>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              {project.genre}
            </p>
          </div>

          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5 shrink-0" />
            <span>{formatDate(project.lastModifiedAt)}</span>
          </div>
        </div>

        <CardFooter className="mt-auto px-5 pb-5 pt-0">
          <Button className="w-full gap-2" onClick={onOpen} type="button" variant="secondary">
            <FolderOpen className="h-4 w-4" />
            {t("projects.actions.open")}
          </Button>
        </CardFooter>
      </Card>

      <ImageLightbox
        alt={coverAlt}
        imageUrl={project.coverImageUrl}
        onOpenChange={setCoverLightboxOpen}
        open={coverLightboxOpen}
        title={project.title}
      />
    </>
  );
}
